import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useNcrStore from '../store/ncrStore';
import useProjectStore from '../store/projectStore';
import useAuthStore from '../store/authStore';
import useUIStore from '../store/uiStore';
import { getDepartments } from '../services/employeeService';

const initialForm = {
  project_id: '', punch_point_id: '', inspection_id: '', ncr_type: 'manufacturing_quality',
  description: '', severity: 'medium', responsible_dept_id: '',
};

const CreateNCRPage = () => {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(initialForm);
  const [departments, setDepartments] = useState([]);
  const { projects, fetchProjects } = useProjectStore();
  const { createNCR, isSubmitting } = useNcrStore();
  const { user } = useAuthStore();
  const showToast = useUIStore((s) => s.showToast);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects();
    getDepartments().then((res) => setDepartments(res.data.departments || [])).catch(() => {});

    const project_id = searchParams.get('project_id');
    const inspection_id = searchParams.get('inspection_id');
    const description = searchParams.get('description');
    if (project_id || inspection_id || description) {
      setForm((f) => ({
        ...f,
        project_id: project_id || f.project_id,
        inspection_id: inspection_id || f.inspection_id,
        description: description || f.description,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.project_id || !form.description) {
      showToast('Project and description are required', 'error');
      return;
    }

    const payload = {
      project_id: Number(form.project_id),
      punch_point_id: form.punch_point_id ? Number(form.punch_point_id) : null,
      inspection_id: form.inspection_id ? Number(form.inspection_id) : null,
      ncr_type: form.ncr_type,
      description: form.description,
      severity: form.severity,
      raised_by_id: user?.id,
      responsible_dept_id: form.responsible_dept_id ? Number(form.responsible_dept_id) : null,
    };

    const result = await createNCR(payload);
    if (result.success) {
      showToast(`NCR created: ${result.data.ncr_number}`, 'success');
      navigate(`/ncr/${result.data.ncr_id}`);
    } else {
      showToast(result.error, 'error');
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-2xl mx-auto glass-card p-6">
        <h1 className="text-xl font-bold text-gray-900 mb-6">Raise NCR</h1>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project *</label>
            <select
              required
              value={form.project_id}
              onChange={handleChange('project_id')}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="">Select a project</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">NCR Type</label>
            <select
              value={form.ncr_type}
              onChange={handleChange('ncr_type')}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="manufacturing_quality">Manufacturing Quality</option>
              <option value="site_issue">Site Issue</option>
              <option value="procurement">Procurement</option>
              <option value="design">Design</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
            <textarea
              required
              rows={3}
              value={form.description}
              onChange={handleChange('description')}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              placeholder="Describe the non-conformance..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Severity *</label>
              <select
                value={form.severity}
                onChange={handleChange('severity')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Responsible Department</label>
              <select
                value={form.responsible_dept_id}
                onChange={handleChange('responsible_dept_id')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option value="">Unassigned</option>
                {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => navigate('/ncr')}
              className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60"
            >
              {isSubmitting ? 'Creating...' : 'Raise NCR'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateNCRPage;
